import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import { Reveal } from "@/components/Reveal";
import { POSTS } from "@/lib/blogData";

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  usePageMeta(
    post ? `${post.title} | Talentify Insights` : "Insights | Talentify",
    post?.excerpt
  );

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <main>
      <article className="relative overflow-hidden pb-24 pt-40">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% -20%, rgba(99,102,241,0.1), transparent 55%)" }} />
        <div className="relative mx-auto max-w-3xl px-6">
          <Reveal>
            <Link to="/blog" data-testid="blog-back-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-400 transition-colors hover:text-sky-400">
              <ArrowLeft size={15} className="transition-transform duration-300 group-hover:-translate-x-1" /> All insights
            </Link>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-8 flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em]">
              <span className="rounded-full border border-sky-400/25 bg-sky-400/[0.07] px-3 py-1 text-sky-300">{post.category}</span>
              <span className="text-slate-500">{post.date}</span>
              <span className="flex items-center gap-1 text-slate-500"><Clock size={11} /> {post.readTime}</span>
            </div>
            <h1 data-testid="blog-post-title" className="mt-5 font-display text-3xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-4xl">
              {post.title}
            </h1>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-6 border-l-2 border-sky-400/50 pl-5 text-base leading-relaxed text-slate-300">{post.excerpt}</p>
          </Reveal>
          <div className="mt-12 space-y-12">
            {post.sections.map((s, i) => (
              <Reveal key={i} delay={0.05}>
                <section>
                  {s.h && <h2 className="mb-4 font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">{s.h}</h2>}
                  {s.paras?.map((p, j) => (
                    <p key={j} className="mb-4 text-[15px] leading-relaxed text-slate-400">{p}</p>
                  ))}
                  {s.bullets && (
                    <ul className="space-y-3">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-300">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.7)]" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.05}>
            <div className="glass-card mt-16 flex flex-col items-start justify-between gap-6 rounded-3xl p-9 sm:flex-row sm:items-center">
              <div>
                <h3 className="font-display text-xl font-bold tracking-tight text-white">Hiring this quarter?</h3>
                <p className="mt-2 text-sm text-slate-400">Talk to our recruitment specialists about your mandate.</p>
              </div>
              <Link
                to="/contact"
                data-testid="blog-post-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-blue-500"
              >
                Talk to Our Experts <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </article>
    </main>
  );
}
